import Router from "vue-router";
import { useLoginStore } from "src/stores/loginStore";

// Eager imports — needed immediately on every page load
import DashboardLayout from "src/components/DashboardLayout.vue";
import Login from "src/pages/Login.vue";
import NotFound from "src/pages/NotFoundPage.vue";

// Lazy imports — loaded on demand when the route is visited
const Dashboard = () => import(/* webpackChunkName: "dashboard" */ "src/pages/DashboardWrapper.vue");
const Caregivers = () => import(/* webpackChunkName: "caregivers" */ "src/pages/Caregivers.vue");
const CaregiverDetails = () => import(/* webpackChunkName: "caregivers" */ "src/pages/CaregiverDetails.vue");
const Clients = () => import(/* webpackChunkName: "clients" */ "src/pages/Clients.vue");
const ClientDetails = () => import(/* webpackChunkName: "clients" */ "src/pages/ClientDetails.vue");
const Shifts = () => import(/* webpackChunkName: "shifts" */ "src/pages/Shifts.vue");
const Calendar = () => import(/* webpackChunkName: "calendar" */ "src/pages/Calendar/Calendar.vue");
const Messages = () => import(/* webpackChunkName: "messages" */ "src/pages/Messages.vue");
const Authorizations = () => import(/* webpackChunkName: "authorizations" */ "src/pages/Authorizations.vue");
const AuthorizationDetails = () => import(/* webpackChunkName: "authorizations" */ "src/pages/AuthorizationDetails.vue");
const InvoiceDetails = () => import(/* webpackChunkName: "authorizations" */ "src/pages/InvoiceDetails.vue");
const AdminUsers = () => import(/* webpackChunkName: "admin" */ "src/pages/AdminUsers.vue");
const AdminDetails = () => import(/* webpackChunkName: "admin" */ "src/pages/AdminDetails.vue");
const Counselors = () => import(/* webpackChunkName: "admin" */ "src/pages/Counselors.vue");
const Offices = () => import(/* webpackChunkName: "admin" */ "src/pages/Offices.vue");
const Payroll = () => import(/* webpackChunkName: "admin" */ "src/pages/Payroll.vue");
const Settings = () => import(/* webpackChunkName: "admin" */ "src/pages/Settings.vue");
const TimeOffRequests = () => import(/* webpackChunkName: "timeoff" */ "src/pages/TimeOffRequests.vue");
const Reports = () => import(/* webpackChunkName: "reports" */ "src/pages/Reports.vue");
const PaymentTracking = () => import(/* webpackChunkName: "reports" */ "src/pages/PaymentTracking.vue");
const CbsPrograms = () => import(/* webpackChunkName: "programs" */ "src/pages/CbsPrograms.vue");
const CstEditor = () => import(/* webpackChunkName: "programs" */ "src/pages/CstEditor.vue");
const ComplianceDashboard = () => import(/* webpackChunkName: "compliance" */ "src/pages/ComplianceDashboard.vue");
const ComplianceSign = () => import(/* webpackChunkName: "compliance" */ "src/pages/ComplianceSign.vue");
const CompliancePortal = () => import(/* webpackChunkName: "compliance" */ "src/pages/CompliancePortal.vue");
const Verification = () => import(/* webpackChunkName: "verification" */ "src/pages/Verification.vue");
const AuditLogs = () => import(/* webpackChunkName: "audit" */ "src/pages/AuditLogs.vue");
const Help = () => import(/* webpackChunkName: "help" */ "src/pages/Help.vue");
const ForgotPassword = () => import(/* webpackChunkName: "auth" */ "src/pages/ForgotPassword.vue");
const ResetPassword = () => import(/* webpackChunkName: "auth" */ "src/pages/ResetPassword.vue");
const Support = () => import(/* webpackChunkName: "static" */ "src/pages/Support.vue");
const Privacy = () => import(/* webpackChunkName: "static" */ "src/pages/Privacy.vue");

let pagesMenu = {
  path: "/pages",
  component: DashboardLayout,
  redirect: "/pages/dashboard",
  children: [
    {
      path: "dashboard",
      name: "Dashboard",
      component: Dashboard,
    },
    {
      path: "messages",
      name: "Messages",
      component: Messages,
    },
    {
      path: "authorizations",
      name: "Authorizations",
      component: Authorizations,
      meta: { requiresNonCaregiver: true },
    },
    {
      path: "authorizations/:id",
      name: "AuthorizationDetails",
      component: AuthorizationDetails,
      meta: { requiresNonCaregiver: true },
    },
    {
      path: "authorizations/:authorizationId/invoices/:year/:month",
      name: "InvoiceDetails",
      component: InvoiceDetails,
      meta: { requiresNonCaregiver: true },
    },
    {
      path: "shifts",
      name: "Shifts",
      component: Shifts,
    },
    {
      path: "time-off",
      name: "TimeOffRequests",
      component: TimeOffRequests,
    },
    {
      path: "calendar",
      name: "Calendar",
      component: Calendar,
    },
    {
      path: "caregivers",
      name: "Caregivers",
      component: Caregivers,
    },
    {
      path: "caregivers/:id",
      name: "CaregiverDetails",
      component: CaregiverDetails,
    },
    {
      path: "clients",
      name: "Clients",
      component: Clients,
    },
    {
      path: "clients/:id",
      name: "ClientDetails",
      component: ClientDetails,
    },
    {
      path: "clients/:clientId/authorizations/:id",
      name: "ClientAuthorizationDetails",
      component: AuthorizationDetails,
      meta: { requiresNonCaregiver: true },
    },
    {
      path: "clients/:clientId/authorizations/:authorizationId/invoices/:year/:month",
      name: "ClientInvoiceDetails",
      component: InvoiceDetails,
      meta: { requiresNonCaregiver: true },
    },
    {
      path: "counselors",
      name: "Counselors",
      component: Counselors,
      meta: { requiresNonCaregiver: true },
    },
    {
      path: "offices",
      name: "Offices",
      component: Offices,
      meta: { requiresNonCaregiver: true },
    },
    {
      path: "admin",
      name: "Admin",
      component: AdminUsers,
      meta: { requiresAdmin: true },
    },
    {
      path: "admin/:id",
      name: "AdminDetails",
      component: AdminDetails,
      meta: { requiresAdmin: true },
    },
    {
      path: "payroll",
      name: "Payroll",
      component: Payroll,
      meta: { requiresNonCaregiver: true },
    },
    {
      path: "reports",
      name: "Reports",
      component: Reports,
      meta: { requiresNonCaregiver: true },
    },
    {
      path: "payment-tracking",
      name: "PaymentTracking",
      component: PaymentTracking,
      meta: { requiresAdmin: true },
    },
    {
      path: "settings/:section?",
      name: "Settings",
      component: Settings,
      meta: { requiresAdmin: true },
    },
    {
      path: "audit-logs",
      name: "AuditLogs",
      component: AuditLogs,
      meta: { requiresAdmin: true },
    },
    {
      path: "cbs-programs",
      name: "CbsPrograms",
      component: CbsPrograms,
      meta: { requiresNonCaregiver: true },
    },
    {
      path: "cst-editor/:id",
      name: "CstEditor",
      component: CstEditor,
    },
    {
      path: "compliance-dashboard",
      name: "ComplianceDashboard",
      component: ComplianceDashboard,
      meta: { requiresNonCaregiver: true },
    },
    {
      path: "help/:articleId?",
      name: "Help",
      component: Help,
      meta: { requiresNonCaregiver: true },
    },
    {
      path: "verification",
      name: "Verification",
      component: Verification,
      meta: { requiresNonCaregiver: true },
    },
  ],
};

let loginPage = {
  path: "/login",
  name: "Login",
  component: Login,
};

let forgotPasswordPage = {
  path: "/forgot-password",
  name: "ForgotPassword",
  component: ForgotPassword,
};

let resetPasswordPage = {
  path: "/reset-password",
  name: "ResetPassword",
  component: ResetPassword,
};

let supportPage = {
  path: "/support",
  name: "Support",
  component: Support,
};

let privacyPage = {
  path: "/privacy",
  name: "Privacy",
  component: Privacy,
};

let complianceSignPage = {
  path: "/sign/:token",
  name: "ComplianceSign",
  component: ComplianceSign,
};

let compliancePortalPage = {
  path: "/compliance/:token",
  name: "CompliancePortal",
  component: CompliancePortal,
};

const routes = [
  {
    path: "/",
    redirect: "/login",
  },
  loginPage,
  forgotPasswordPage,
  resetPasswordPage,
  supportPage,
  privacyPage,
  complianceSignPage,
  compliancePortalPage,
  pagesMenu,
  {
    path: "*",
    component: NotFound,
  },
];

export const router = new Router({
  mode: "history",
  linkActiveClass: "active",
  base: "/",
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

router.beforeEach((to, from, next) => {
  const publicPages = ["/login", "/forgot-password", "/reset-password", "/support", "/privacy"];
  const isPublic = publicPages.includes(to.path) || to.path.startsWith("/sign/") || to.path.startsWith("/compliance/");
  const requiresAuth = !isPublic;
  const store = useLoginStore();

  if (requiresAuth && !store.getLoggedIn) {
    return next("/login");
  }

  // Check if route requires admin access
  if (to.matched.some((record) => record.meta.requiresAdmin)) {
    if (!store.isAdmin) {
      return next("/pages/dashboard");
    }
  }

  // Check if route requires non-caregiver access
  if (to.matched.some((record) => record.meta.requiresNonCaregiver)) {
    if (store.isCaregiver) {
      return next("/pages/dashboard");
    }
  }

  next();
});

export default router;
